/**
 * Arrows in flight.
 *
 * The first thing in this world that moves on its own. Pure physics and pure
 * geometry: a position, a velocity, gravity, and a segment test against the
 * blocks each step. Nothing here knows what an arrow looks like or which
 * button fired it.
 *
 * An arrow is stepped as a *segment*, not as a point that teleports. At forty
 * blocks a second and sixty frames a second it covers two thirds of a block
 * per frame, and a point sampled once a frame walks straight through a wall
 * one block thick. Marching the segment is also what lets it hit a villager
 * standing in the gap between two samples.
 */

import { raycastVoxels } from "../physics/raycast.js";
import { pickEntity, type Positioned } from "./aim.js";

/** Blocks per second as it leaves the bow. */
export const ARROW_SPEED = 42;
/** Blocks per second squared. Lower than the player's, so the arc reads. */
export const ARROW_GRAVITY = 18;
/** What an arrow does to whatever it lands in. */
export const ARROW_DAMAGE = 4;

/** How long an arrow stays stuck where it landed, in seconds. */
export const ARROW_LIFETIME = 15;

export interface Arrow {
  readonly id: number;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  /** Seconds it has been stuck. Negative while it is still flying. */
  stuck: number;
}

let nextId = 1;

export function makeArrow(
  from: { x: number; y: number; z: number },
  dir: { x: number; y: number; z: number },
  speed = ARROW_SPEED,
): Arrow {
  return {
    id: nextId++,
    x: from.x, y: from.y, z: from.z,
    vx: dir.x * speed, vy: dir.y * speed, vz: dir.z * speed,
    stuck: -1,
  };
}

export function isFlying(arrow: Arrow): boolean {
  return arrow.stuck < 0;
}

/** Which way an arrow points, for drawing it. Flying: along its velocity. */
export function arrowAim(arrow: Arrow): { yaw: number; pitch: number } {
  const flat = Math.hypot(arrow.vx, arrow.vz);
  return {
    yaw: Math.atan2(-arrow.vx, -arrow.vz),
    pitch: Math.atan2(arrow.vy, flat),
  };
}

export interface ArrowHit<T> {
  arrow: Arrow;
  target: T;
}

/**
 * How wide and tall the things an arrow can hit are.
 *
 * One box for everything alive. A villager and an orc differ by a hand's
 * width, and an arrow that misses one and hits the other because of it would
 * be a distinction nobody could see and everybody would feel.
 */
export const TARGET_WIDTH = 0.7;
export const TARGET_HEIGHT = 1.9;

/**
 * Advance every arrow by `dt`, and say who was hit.
 *
 * Arrows that have been stuck longer than ARROW_LIFETIME are removed from the
 * list in place - an arrow nobody can pick up and nobody can see is only a
 * slow leak.
 */
export function stepArrows<T extends Positioned>(
  arrows: Arrow[],
  dt: number,
  isSolid: (x: number, y: number, z: number) => boolean,
  targets: readonly T[],
): ArrowHit<T>[] {
  const hits: ArrowHit<T>[] = [];

  for (let i = arrows.length - 1; i >= 0; i--) {
    const arrow = arrows[i]!;

    if (!isFlying(arrow)) {
      arrow.stuck += dt;
      if (arrow.stuck > ARROW_LIFETIME) arrows.splice(i, 1);
      continue;
    }

    arrow.vy -= ARROW_GRAVITY * dt;

    const dx = arrow.vx * dt;
    const dy = arrow.vy * dt;
    const dz = arrow.vz * dt;
    const distance = Math.hypot(dx, dy, dz);
    if (distance <= 0) continue;

    const nx = dx / distance;
    const ny = dy / distance;
    const nz = dz / distance;

    // Whoever is nearer along the segment wins. Checking blocks first and
    // people second would let an arrow stop in the wall behind somebody
    // standing in front of it.
    const struck = pickEntity(
      targets, arrow.x, arrow.y, arrow.z, nx, ny, nz, distance,
      TARGET_WIDTH, TARGET_HEIGHT,
    );
    const wall = raycastVoxels(
      arrow.x, arrow.y, arrow.z, nx, ny, nz, distance, isSolid,
    );

    if (struck !== null && (wall === null || struck.distance <= wall.distance)) {
      arrow.x += nx * struck.distance;
      arrow.y += ny * struck.distance;
      arrow.z += nz * struck.distance;
      arrow.stuck = 0;
      hits.push({ arrow, target: struck.entity });
      /*
       * Gone, not stuck.
       *
       * A wall stays where it is, so an arrow standing in one looks right for
       * the fifteen seconds it lasts. A person does not: an orc's arrow hit
       * the player, planted itself at the spot where the player had been, and
       * hung there in mid-air while they walked away from it. The same was
       * true of every animal shot.
       *
       * Making it follow the body is the prettier answer and wants the arrow
       * to know whose body it is - a reference, in a file that is otherwise
       * pure arithmetic, for a shaft you see for a moment. An arrow that goes
       * in is gone.
       */
      arrows.splice(i, 1);
      continue;
    }

    if (wall !== null) {
      // Stop just short of the face, so the shaft is visible standing in the
      // block rather than buried inside it.
      const back = Math.max(0, wall.distance - 0.08);
      arrow.x += nx * back;
      arrow.y += ny * back;
      arrow.z += nz * back;
      arrow.stuck = 0;
      continue;
    }

    arrow.x += dx;
    arrow.y += dy;
    arrow.z += dz;

    // An arrow that leaves the world is gone; nothing below the floor of it
    // will ever be seen again.
    if (arrow.y < -8) arrows.splice(i, 1);
  }

  return hits;
}
